import { useState } from "react";
import { toast } from "react-toastify";
import PlanLayout from "./components/PlanLayout";
import Drawer from "../../Component/Drawer";
import Pill from "../../Component/Pill";
import { inr } from "../../utils/staticData";

// STATIC DESIGN — add-ons live in local state until the add-on API exists
interface AddOn { n: string; ic: string; t: string; p: number; u: string; el: string[]; buy: number; on: boolean }

const PLAN_NAMES = ["Free", "Starter", "Growth", "Pro", "Enterprise", "Lifetime"];
const SEED: AddOn[] = [
  { n: "Extra agent seat", ic: "ri-user-add-line", t: "recurring", p: 499, u: "per seat / month", el: ["Starter", "Growth", "Pro", "Lifetime"], buy: 612, on: true },
  { n: "Additional WhatsApp number", ic: "ri-whatsapp-line", t: "recurring", p: 999, u: "per number / month", el: ["Growth", "Pro", "Enterprise"], buy: 188, on: true },
  { n: "Extra 10K contacts", ic: "ri-contacts-book-line", t: "recurring", p: 299, u: "per 10K / month", el: ["Starter", "Growth", "Pro", "Lifetime"], buy: 341, on: true },
  { n: "AI credits pack", ic: "ri-sparkling-2-line", t: "one-time", p: 1999, u: "10,000 AI replies", el: ["Growth", "Pro", "Enterprise"], buy: 274, on: true },
  { n: "Instagram channel", ic: "ri-instagram-line", t: "recurring", p: 799, u: "per account / month", el: ["Starter"], buy: 96, on: true },
  { n: "Green tick assistance", ic: "ri-verified-badge-line", t: "one-time", p: 4999, u: "application support", el: ["Growth", "Pro", "Enterprise", "Lifetime"], buy: 58, on: true },
  { n: "Dedicated account manager", ic: "ri-customer-service-2-line", t: "recurring", p: 9999, u: "per month", el: ["Pro"], buy: 12, on: true },
  { n: "Custom white-label domain", ic: "ri-global-line", t: "recurring", p: 2999, u: "per domain / month", el: ["Pro", "Enterprise"], buy: 0, on: false },
];
const BLANK: AddOn = { n: "", ic: "ri-puzzle-line", t: "recurring", p: 0, u: "", el: [], buy: 0, on: true };

const AddOns = () => {
  const [addons, setAddons] = useState<AddOn[]>(SEED);
  const [type, setType] = useState("all");
  // index being edited; -1 = new add-on; null = drawer closed
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [form, setForm] = useState<AddOn>(BLANK);

  const rows = addons.map((a, i) => ({ a, i })).filter(({ a }) => type === "all" || a.t === type);
  const count = (k: string) => (k === "all" ? addons.length : addons.filter(a => a.t === k).length);

  const open = (i: number) => {
    setForm(i >= 0 ? { ...addons[i], el: [...addons[i].el] } : { ...BLANK, el: [] });
    setEditIdx(i);
  };

  const setLive = (i: number, on: boolean) => {
    setAddons(list => list.map((a, k) => (k === i ? { ...a, on } : a)));
    toast.success(`${addons[i].n} ${on ? "is live" : "hidden"}`);
  };

  const save = () => {
    if (!form.n.trim()) { toast.error("Add-on name is required"); return; }
    setAddons(list => (editIdx === -1 ? [...list, form] : list.map((a, k) => (k === editIdx ? form : a))));
    setEditIdx(null);
    toast.success("Add-on saved");
  };

  const archive = () => {
    setAddons(list => list.filter((_, k) => k !== editIdx));
    setEditIdx(null);
    toast.success("Add-on archived");
  };

  const toggleEl = (p: string) =>
    setForm(f => ({ ...f, el: f.el.includes(p) ? f.el.filter(x => x !== p) : [...f.el, p] }));

  return (
    <PlanLayout actions={
      <button type="button" className="cx-btn cx-btn-primary" onClick={() => open(-1)}><i className="ri-add-line"></i>New add-on</button>
    }>
      <div className="cx-card">
        <div className="cx-toolbar">
          <div className="cx-chips">
            {[["all", "All"], ["recurring", "Recurring"], ["one-time", "One-time"]].map(([k, l]) => (
              <button type="button" key={k} className={`cx-chip ${type === k ? "on" : ""}`} onClick={() => setType(k)}>{l} <b>{count(k)}</b></button>
            ))}
          </div>
        </div>
        <div className="cx-tbl-wrap">
          <table className="cx-tbl">
            <thead>
              <tr><th>Add-on</th><th>Type</th><th className="r">Price</th><th>Eligible plans</th><th className="r">Buyers</th><th>Live</th><th className="r">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length ? rows.map(({ a, i }) => (
                <tr key={i}>
                  <td>
                    <div className="cx-who">
                      <div className="av" style={{ background: "var(--wa-tint)", fontSize: 15 }}><i className={a.ic}></i></div>
                      <div><b>{a.n}</b><small>{a.u}</small></div>
                    </div>
                  </td>
                  <td><Pill tone={a.t === "recurring" ? "blue" : "none"}>{a.t}</Pill></td>
                  <td className="r mono fw6">{inr(a.p)}</td>
                  <td>
                    <div className="cx-chips">
                      {a.el.map(e => <span key={e} className="cx-pill none" style={{ textTransform: "none", fontFamily: "inherit", fontSize: 11 }}>{e}</span>)}
                    </div>
                  </td>
                  <td className="r mono">{a.buy}</td>
                  <td>
                    <label className="cx-switch">
                      <input type="checkbox" checked={a.on} onChange={(e) => setLive(i, e.target.checked)} aria-label={`${a.n} live`} />
                      <span></span>
                    </label>
                  </td>
                  <td>
                    <div className="cx-ra">
                      <button type="button" className="v" title="Edit" onClick={() => open(i)}><i className="ri-edit-line"></i></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7}><div className="cx-empty"><i className="ri-puzzle-line"></i>No add-ons found.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="cx-tfoot">
          <span>The switch shows or hides an add-on in the client app immediately.</span>
          <span>Recurring add-on MRR <b className="mono">{inr(addons.filter(a => a.t === "recurring" && a.on).reduce((s, a) => s + a.p * a.buy, 0))}</b></span>
        </div>
      </div>

      <Drawer
        show={editIdx !== null}
        onClose={() => setEditIdx(null)}
        title={editIdx !== null && editIdx >= 0 ? `Edit add-on · ${addons[editIdx].n}` : "New add-on"}
        subtitle="Shown in the client app's Manage Subscription"
        footer={<>
          {editIdx !== null && editIdx >= 0 && <>
            <button type="button" className="cx-btn cx-btn-danger" onClick={archive}><i className="ri-archive-line"></i>Archive</button>
            <div className="sp"></div>
          </>}
          <button type="button" className="cx-btn cx-btn-light" onClick={() => setEditIdx(null)}>Cancel</button>
          <button type="button" className="cx-btn cx-btn-primary" onClick={save}>Save add-on</button>
        </>}
      >
        <div className="cx-fgrid">
          <div className="full">
            <label className="cx-label">Add-on name</label>
            <input className="cx-input" value={form.n} placeholder="Extra agent seat" onChange={(e) => setForm({ ...form, n: e.target.value })} />
          </div>
          <div>
            <label className="cx-label">Billing type</label>
            <select className="cx-select" value={form.t} onChange={(e) => setForm({ ...form, t: e.target.value })}>
              <option value="recurring">recurring</option>
              <option value="one-time">one-time</option>
              <option value="usage-based">usage-based</option>
            </select>
          </div>
          <div>
            <label className="cx-label">Price (₹, excl. GST)</label>
            <input className="cx-input mono" type="number" min="0" value={form.p || ""} onChange={(e) => setForm({ ...form, p: Math.max(0, +e.target.value || 0) })} />
          </div>
          <div className="full">
            <label className="cx-label">Unit label</label>
            <input className="cx-input" value={form.u} placeholder="per seat / month" onChange={(e) => setForm({ ...form, u: e.target.value })} />
          </div>
          <div className="full">
            <label className="cx-label">Eligible plans</label>
            <div className="cx-chips">
              {PLAN_NAMES.map(p => (
                <button type="button" key={p} className={`cx-chip ${form.el.includes(p) ? "on" : ""}`} onClick={() => toggleEl(p)}>{p}</button>
              ))}
            </div>
          </div>
          <div className="full cx-togrow">
            <label className="cx-switch"><input type="checkbox" checked={form.on} onChange={(e) => setForm({ ...form, on: e.target.checked })} /><span></span></label>
            <span className="fs12">Live — visible in the client app</span>
          </div>
        </div>
      </Drawer>
    </PlanLayout>
  );
};

export default AddOns;
